import { useCallback, useEffect, useMemo, useState } from "react";
import dateformat from "dateformat";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import { useSnackbar } from "$utils/useSnackbar";
import { createUseStyles } from "$utils/createUseStyles";
import { useSelector } from "react-redux";
import { ListAlt } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { Text, InfoContainer, Popin } from "$components";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { Services } from "$utils/typeConstants";
import { snackbarSuccessOptions } from "$utils/SnackbarOptions";
import JBadge from "$components/JBadge/JBadge";
import useOrder, { STATUS_ORDERS } from "$hooks/useOrder";
import JTable from "$components/JTable/JTable";
import OrderForm from "$containers/Orders/forms/OrderForm";
import { EntrepotService, CompanyServices, OrderService } from "$services";
import { appState } from "$reducers";
import useAuth from "$hooks/useAuth";
import { OrderStatus } from "$types";
import { CacheInvalidationAction } from "$utils/cache";
import { useList } from "$hooks/useList";

type Props = {
  dockyardId: string;
  setHasOrders: (newValue: boolean) => void;
};

interface OrdersResponse {
  data: any[];
  meta: {
    companies?: any[];
    warehouses?: any[];
    dockyards?: any[];
    projects?: any[];
    pagination: {
      total: number;
      pageCount: number;
      page: number;
      pageSize: number;
    };
  };
}

function Orders({ dockyardId, setHasOrders }: Props) {
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);
  const { filter: companySidebarFilter } = useSelector(appState);
  const classes = useStyles();
  const { isContributor, token } = useAuth();

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);

  const {
    togglePopin: togglePopinOrder,
    open: openOrder,
    defaultOptions,
    getOrderType,
  } = useOrder();

  const defaultOrderFilters = useMemo(() => {
    return {
      dockyard: dockyardId,
    };
  }, [dockyardId]);

  // Fetch warehouses separately - don't include it in the initial filters
  // We'll add it to the filters after warehouses are loaded
  const { data: entrepotsData } = useQuery({
    queryKey: [
      CacheInvalidationAction.GET_ENTREPOTS,
      { company: companySidebarFilter?._id },
    ],

    queryFn: () =>
      EntrepotService.getEntrepots({
        company: companySidebarFilter?._id,
      }),

    enabled: !!companySidebarFilter?._id,
    gcTime: 5 * 1000 * 60,
  });

  // For the new useList, prepare default sort if needed
  const defaultSort = useMemo(() => {
    if (!defaultOptions.defaultSort) return undefined;

    // If defaultSort is already in the correct format, use it directly
    if (
      typeof defaultOptions.defaultSort === "object" &&
      defaultOptions.defaultSort.field &&
      defaultOptions.defaultSort.direction
    ) {
      return defaultOptions.defaultSort;
    }

    // If it's a string, convert it to the expected format
    if (typeof defaultOptions.defaultSort === "string") {
      const [field, direction = "asc"] = defaultOptions.defaultSort.split(":");
      return {
        field,
        direction: direction as "asc" | "desc",
      };
    }

    return undefined;
  }, [defaultOptions.defaultSort]);

  // Use the consolidated endpoint from gateway instead of direct API call
  const {
    data: orderItems,
    isLoading: isFetchingOrders,
    filters: filtersOrders,
    setFilter: setFilterOrders,
    pagination,
    refetch: fetchOrderItems,
  } = useList<OrdersResponse & { id: string }>(
    "api/order/orders/consolidated",
    {
      defaultFilters: defaultOrderFilters,
      defaultPageSize: defaultOptions.defaultPageSize || 20,
      defaultSort,
      populate: defaultOptions.populate,
      additionalParams: {},
      ignoreSideBarFilter: true,
      service: Services.GATEWAY_API,
      jwt: token,
    }
  );

  useEffect(() => {
    // Once warehouses are loaded, update the filters if needed
    if (isContributor() && entrepotsData?.data?.length) {
      const warehouseIds = entrepotsData.data.map((w: any) => w._id);
      if (warehouseIds.length > 0) {
        setFilterOrders("warehouse", { $in: warehouseIds });
      }
    }
  }, [entrepotsData, isContributor, setFilterOrders]);

  const fields = useMemo(() => {
    // Properly handle item structures with either direct properties or nested attributes
    const orders = ((orderItems as any).data || orderItems || []).map(
      (item: any) => {
        if (item.attributes) {
          // Case: item has nested attributes - combine id with attributes
          return {
            id: item.id,
            ...item.attributes,
          };
        }
        // Case: item already has properties directly
        return item;
      }
    );
    return orders;
  }, [orderItems]);

  const onSubmitOrder = useCallback(
    async (data: any) => {
      setIsSubmittingOrder(true);
      try {
        const order = { ...data };
        if (order.id) {
          await OrderService.updateOrderItem(order.id, order);
          togglePopinOrder(false);
        }
        await fetchOrderItems();
      } catch (error) {
        console.error("Error submitting order:", error);
      } finally {
        setIsSubmittingOrder(false);
      }
    },
    [fetchOrderItems, togglePopinOrder]
  );

  const handleCancelOrder = useCallback(
    async (data: any) => {
      try {
        const order = { ...data, status: OrderStatus.CANCELED };
        await OrderService.updateOrderItem(order.id, order);
        togglePopinOrder(false);
        openSnackbar(
          `La commande ${order.reference} a bien été annulée.`,
          5000
        );
        await fetchOrderItems();
      } catch (error) {
        console.error("Error canceling order:", error);
      }
    },
    [fetchOrderItems, openSnackbar, togglePopinOrder]
  );

  useEffect(() => {
    setHasOrders((orderItems?.length || 0) > 0);
  }, [orderItems, setHasOrders]);

  // Use companies, warehouses from the consolidated response if available
  const getCompanyById = useCallback(
    (companyId: string) => {
      // Try to get from consolidated data first
      if ((orderItems as any)?.meta?.companies) {
        const foundCompany = (orderItems as any).meta.companies.find(
          (c: any) => c._id === companyId
        );
        if (foundCompany) return foundCompany;
      }

      // Fall back to query if needed
      return null;
    },
    [orderItems]
  );

  const getWarehouseById = useCallback(
    (warehouseId: string) => {
      // Try to get from consolidated data first
      if ((orderItems as any)?.meta?.warehouses) {
        const foundWarehouse = (orderItems as any).meta.warehouses.find(
          (w: any) => w._id === warehouseId
        );
        if (foundWarehouse) return foundWarehouse;
      }

      // Fall back to query if needed
      return null;
    },
    [orderItems]
  );

  const columnsOrders = useMemo(
    () => [
      {
        Header: "Entreprise",
        maxWidth: 180,
        minWidth: 180,
        Cell: ({
          row: {
            original: { company: orderCompany },
          },
        }: any) => {
          // Try to get company from consolidated data first
          const companyFromConsolidated = getCompanyById(orderCompany);

          // Only run the query if we couldn't find the company in consolidated data
          const { data: orderCompanyId } = useQuery({
            queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, orderCompany],
            queryFn: () => CompanyServices.getCompanyById(orderCompany),
            enabled: !!orderCompany && !companyFromConsolidated,
            staleTime: 60 * 1000,
          });

          const orderCompanyName =
            companyFromConsolidated?.name || orderCompanyId?.name || "N/A";

          return (
            <Text
              type="important"
              style={{
                whiteSpace: "nowrap",
                display: "inline-block",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {orderCompanyName}
            </Text>
          );
        },
      },
      {
        Header: "Date commande",
        minWidth: 130,
        maxWidth: 130,
        Cell: ({
          row: {
            original: { orderDate },
          },
        }: any) => <Text>{dateformat(orderDate, DEFAULT_DATE_FORMAT)}</Text>,
      },
      {
        Header: "Fournisseur",
        Cell: ({
          row: {
            original: { provider },
          },
        }: any) => {
          // Try to get provider from consolidated data first
          const providerFromConsolidated = getCompanyById(provider);

          // Only run the query if we couldn't find the provider in consolidated data
          const { data: company } = useQuery({
            queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, provider],
            queryFn: () => CompanyServices.getCompanyById(provider),
            enabled: !!provider && !providerFromConsolidated,
            staleTime: 60 * 1000,
          });

          const providerName =
            providerFromConsolidated?.name ||
            company?.name ||
            provider ||
            "N/A";

          return <Text>{providerName}</Text>;
        },
      },
      {
        Header: "Entrepôt",
        Cell: ({
          row: {
            original: { warehouse, company: orderCompany },
          },
        }: any) => {
          // Try to get warehouse from consolidated data first
          const warehouseFromConsolidated = getWarehouseById(warehouse);

          // Try to get company from consolidated data
          const companyFromConsolidated = getCompanyById(orderCompany);

          // Only run the company query if needed and not found in consolidated data
          const { data: company } = useQuery({
            queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, orderCompany],
            queryFn: () => CompanyServices.getCompanyById(orderCompany),
            enabled: !!orderCompany && !companyFromConsolidated,
            staleTime: 60 * 1000,
          });

          // Only run the warehouse query if needed and not found in consolidated data
          const { data: warehouseData } = useQuery({
            queryKey: [
              CacheInvalidationAction.GET_ENTREPOT_BY_ID,
              warehouse,
              (companyFromConsolidated || company)?._id,
            ],

            queryFn: () => EntrepotService.getEntrepotById(warehouse),
            enabled: !!warehouse && !warehouseFromConsolidated,
            staleTime: 60 * 1000,
          });

          const orderWarehouseName =
            warehouseFromConsolidated?.name || warehouseData?.name || "N/A";

          return <Text>{orderWarehouseName}</Text>;
        },
      },
      {
        Header: "N° de commande",
        maxWidth: 120,
        minWidth: 120,
        Cell: ({
          row: {
            original: { reference },
          },
        }: any) => <Text>{reference}</Text>,
      },
      {
        Header: () => (
          <>
            Type
            <br />
            appro
          </>
        ),
        accessor: "type",
        maxWidth: 90,
        minWidth: 90,
        Cell: ({
          row: {
            original: { suborders },
          },
        }: any) => (
          <div>
            {suborders?.length ? (
              <Text>{getOrderType(suborders)}</Text>
            ) : (
              <Text>Aucun</Text>
            )}
          </div>
        ),
      },
      {
        Header: "Statut",
        minWidth: 100,
        maxWidth: 100,
        Cell: ({ row: { original } }: any) => (
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <JBadge variant={original?.status} messages={STATUS_ORDERS} />
            <Text> {original?.statusModifiedDate}</Text>
          </div>
        ),
      },
      {
        Header: "Conformité",
        minWidth: 90,
        maxWidth: 90,
        Cell: ({
          row: {
            original: { conformity },
          },
        }: any) => (
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {conformity === null ? null : conformity === true ? (
              <SentimentSatisfiedAltIcon style={{ color: "green" }} />
            ) : (
              <SentimentVeryDissatisfiedIcon style={{ color: "tomato" }} />
            )}
          </div>
        ),
      },
    ],
    [getOrderType, getCompanyById, getWarehouseById]
  );

  return (
    <>
      <InfoContainer
        leftHeader={
          <div className={classes.infoHeader}>
            <ListAlt />
            <Text type="h3">Commandes</Text>
          </div>
        }
        className={classes.infoContainer}
      >
        <JTable
          data={fields ?? []}
          columns={columnsOrders}
          isFetching={isFetchingOrders}
          pageCount={pagination.pageCount}
          filters={filtersOrders}
          setFilter={setFilterOrders}
          setPageIndex={pagination.setPageIndex}
          pageIndex={pagination.pageIndex}
          onClickAction={() => {
            togglePopinOrder(true);
          }}
          setSelectedItem={setSelectedOrder}
          pageName="dockyard-orderList"
          hideFilters
          hideCheckboxes
        />
      </InfoContainer>

      <Popin
        openPopin={openOrder}
        toggleDrawer={togglePopinOrder}
        minWidth="70%"
      >
        <OrderForm
          order={selectedOrder}
          onSubmit={onSubmitOrder}
          onCancelOrder={handleCancelOrder}
          onReset={() => togglePopinOrder(false)}
          isSubmitting={isSubmittingOrder}
        />
      </Popin>
    </>
  );
}

export default Orders;

const useStyles = createUseStyles({
  infoHeader: {
    display: "flex",
    alignItems: "center",
    "& h3": {
      margin: "0 8px",
    },
  },
  infoContainer: {
    marginTop: 10,
    marginBottom: 20,
  },
});
